import { useCallback, useEffect, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { EventTaskCompletedIcon, EventTaskFutureIcon, EventTaskUncompletedIcon } from '../../Icons/IconsLib';
import { Flex, ProgressCircle, Content, TableView, TableHeader, TableBody, Column, Row, Cell, View, Link, DialogContainer } from '@adobe/react-spectrum';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import useComonentReload from '../../../../hooks/UseComponentReload';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { CircleFill } from 'react-bootstrap-icons';
import { CLOSED_TITLES, normalizeText, statusColor } from '../../../../utils/helperUtil';
import { useTableViewHeight } from '../../../../utils/tableViewUtils';
import { ListEventRequest } from '../../../../services/soap/scheduler/requests/ListEventRequest';
import { GetSchedulerSettingsRequest, SettingsItemType } from '../../../../services/soap/scheduler/requests/GetSchedulerSettingsRequest';
import styles from './events_list.module.css';
import EventListTable from '../../../../pages/protected/calendar/components/EventListTable';
import AddUpdateEvent from '../../../../pages/protected/calendar/components/AddUpdateEvent';
import { ListUserRequest } from '../../../../services/soap/access/requests/ListUserRequest';
import { GetEventRequest } from '../../../../services/soap/scheduler/requests/GetEventRequest';
import { getTypeOfWorkFromLogin, TypeOfWorkBinding } from '../../../../utils/LoginUtils';

interface EventsListComponentProps {
  selectedItem?: FPAData;
}

function EventsList({ selectedItem }: Readonly<EventsListComponentProps>) {
  const { t } = useTranslation('calendar');

  let columnsData = [
    {
      key: 'icon',
      name: '',
      width: 50,
    },
    {
      key: 'dateTo',
      name: t('date', { ns: 'layout_components' }),
      width: 170,
    },
    {
      key: 'solutionStatus',
      name: t('status', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'title',
      name: t('subject', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'userList',
      name: t('user', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'entryTypeName',
      name: t('type_of_work', { ns: 'layout_components' }),
      width: null,
    },
  ];

  let columnsDataMobile = [
    {
      key: 'icon',
      name: '',
      width: 25,
    },
    {
      key: 'dateTo',
      name: t('date', { ns: 'layout_components' }),
      width: 170,
    },
    {
      key: 'solutionStatus',
      name: t('status', { ns: 'layout_components' }),
      width: 150,
    },
    {
      key: 'title',
      name: t('subject', { ns: 'layout_components' }),
      width: 250,
    },
    {
      key: 'userList',
      name: t('user', { ns: 'layout_components' }),
      width: 250,
    },
    {
      key: 'entryTypeName',
      name: t('type_of_work', { ns: 'layout_components' }),
      width: 150,
    },
  ];
  const [reloadComponent] = useComonentReload();
  const { schedulerService, store, accessService } = useDependency();
  const { isMobile } = useViewInfo();
  const [showLoader, setShowLoader] = useState(false);
  const [rowsData, setRowsData] = useState<any>([]);
  const [upComing7DaysEvents, setUpComing7DaysEvents] = useState<any>([]);
  const [past7DaysCompletedEvents, setPast7DaysCompletedEvents] = useState<any>([]);
  const [past3MonthsUnCompletedEvents, setPast3MonthsUnCompletedEvents] = useState<any>([]);
  const _t = (key: string) => t(`add_update_event.${key}`, { ns: 'calendar' });
  const [showAdd, setShowAdd] = useState<any>();
  const [isEdit, setIsEdit] = useState<any>();
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [typeOfWorkList, setTypeOfWorkList] = useState<any>([]);
  const [eventStatusList, setEventStatusList] = useState<any>([]);
  const [userList, setUserList] = useState<any>([]);
  const [selectedDataRow, setSelectedDataRow] = useState<any>();
  const { tableHeight, containerHeight, tableStyles } = useTableViewHeight(
    rowsData.length,
    {
      headerHeight: 50,
      rowHeight: 50,
      maxVisibleRows: 5,
      containerExtraSpace: 50,
      maxContainerHeight: 400,
      showOverflow: false
    }
  );

  useEffect(() => {
    loadEventList();
    getUserData();
    getTypeofWorkList();
  }, [selectedItem, reloadComponent]);

  const getUserCount = async () => {
    let userList = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, undefined, undefined, '1'));
    if (userList.result === 'OK') {
      return userList.count;
    }
  }

  const getUserData = async () => {
    const totalRecords = await getUserCount();
    let userResp = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, totalRecords));
    if (userResp.result === 'OK') {
      const formattedArray = userResp.USERS.map(({ USER }) => ({
        id: USER.id,
        name: `${USER.name1} ${USER.name3}`
      }));

      setUserList(formattedArray);
      return formattedArray
    }
  }

  const getEventStatusList = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await schedulerService.getSchedulerSettings(new GetSchedulerSettingsRequest(
      store.Server,
      store.SessionId,
      '1',
      SettingsItemType.EVENT_ITEM_TYPE,
      300,
      0));

    if (res.result === 'OK' && res.ITEMS.length > 0) {
      res?.ITEMS.forEach((status: any) => {
        const { ident, title, color } = status.ITEM;
        let colorString = statusColor(color);
        statusArray.push({ ident, title, color: colorString });
      });

      statusArray.unshift({ ident: 'All states', title: '', color: 'black' });
      setEventStatusList(statusArray);
      return statusArray;
    }
  }, []);

  const getTypeofWorkList = async () => {
    const bindedTypeOfWork = getTypeOfWorkFromLogin(TypeOfWorkBinding.Event, store.session.organization);
    const statusArray: any = []
    // let scheduleResp = await schedulerService.getSchedulerSettings(new GetSchedulerSettingsRequest(
    //   store.Server,
    //   store.SessionId,
    //   '4',
    //   SettingsItemType.EVENT_ITEM_TYPE,
    //   50,
    //   0
    // ));

    // if (scheduleResp.result === "OK") {
      
    // }

    bindedTypeOfWork?.forEach((status: any) => {
        const { ident, title, color,defaultFor } = status;
        let colorString = statusColor(color);
        statusArray.push({ ident, title, color: colorString,defaultFor });
      });

      setTypeOfWorkList(statusArray);

  }

  const parseDateTo = (s: string) =>
    moment(s, ['MM/DD/YYYY | hh:mm A', 'MM/DD/YYYY | HH:mm A', 'MM/DD/YYYY | HH:mm'], true);
  const parseDay = (s: string) => moment(s.split('|')[0].trim(), 'MM/DD/YYYY', true);
  const addDays = (d: Date, days: number) => {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
  };

  const startOfToday = () => moment().startOf('day');

  const isTodayOrFuture = (e: any) => {
    const d = parseDay(e.dateTo); // uses MM/DD/YYYY from "dateTo"
    return d.isValid() && d.isSameOrAfter(startOfToday(), 'day');
  };

  const isPastStrict = (e: any) => {
    const d = parseDay(e.dateTo);
    return d.isValid() && d.isBefore(startOfToday(), 'day');
  };


  const handleAddNewClose = () => {
    setShowAdd(false);
  }

  const refreshDetailView = () => {
    loadEventList();
  }

  const handleRefreshAddNewEvent = () => {
    loadEventList();
  }

  const loadEventsFuturList = async (statusArray: any, standardStatus: any, startDate: any, endDate: any, eventListtype: any) => {
    return loadEvents(statusArray, standardStatus, startDate, endDate, eventListtype);
  };

  const loadEventsPastLineCompleted = async (statusArray: any) => {
    const start = addDays(new Date(), -7); // 7 days past
    return loadEvents(statusArray, 1, start, new Date(), 'pastCompleted');
  };

  const loadEventsPastLineUnCompleted = async (statusArray: any) => {
    const start = addDays(new Date(), -90); // 3M days past
    return loadEvents(statusArray, "0", start, new Date(), 'pastUnCompleted');
  };

  const getStatusColor = (statusArray: any, solutionStatus: any) => {
    const statusObj = statusArray.find((obj: any) => obj.ident == solutionStatus);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusObj?.title}
      </>
    );
  };

  const loadEventList = async () => {
    (async () => {
      getEventStatusList().then(async (statusArray: any) => {
        const end = addDays(new Date(), 7);
        const [eventsFuture, eventsPastCompleted, eventsPastUncompleted] = await Promise.all([
          loadEventsFuturList(statusArray, undefined, new Date(), end, 'future'),
          loadEventsPastLineCompleted(statusArray),
          loadEventsPastLineUnCompleted(statusArray),
        ]);


        const pastCompletedFiltered = eventsPastCompleted.filter(isPastStrict);
        const pastUncompletedFiltered = eventsPastUncompleted.filter(isPastStrict);

        const sortByDateDesc = (arr: any[]) =>
          [...arr].sort(
            (a, b) => parseDateTo(b.dateTo).valueOf() - parseDateTo(a.dateTo).valueOf()
          );

        const byAsc = (a: any, b: any) =>
          parseDateTo(a.dateTo).valueOf() - parseDateTo(b.dateTo).valueOf();

        const anchor: moment.Moment = (() => {
          const vals = eventsFuture
            .map((e: any) => parseDay(e.dateTo))
            .filter((m: moment.Moment) => m.isValid())
            .map((m: any) => m.startOf('day').valueOf());
          const min = Math.min(...vals);
          return Number.isFinite(min) ? moment(min) : moment().startOf('day');
        })();

        const dayDiff = (e: any) => {
          const d = parseDay(e.dateTo);
          return d.isValid() ? d.diff(anchor, 'days') : Number.NaN;
        };

        const dateFormat = (d: moment.Moment) => {
          var dayOfWeek = [_t('sunday'), _t('monday'), _t('tuesday'), _t('wednesday'), _t('thursday'), _t('friday'), _t('saturday')];
          var monthNames = [_t('jan'), _t('feb'), _t('mar'), _t('apr'), _t('may'), _t('jun'), _t('jul'), _t('aug'), _t('sep'), _t('oct'), _t('nov'), _t('dec')];
          return `${dayOfWeek[d.day()]} ${d.date()} ${monthNames[d.month()]} ${d.year()}`;
        };

        const todayArr = eventsFuture.filter((e: any) => dayDiff(e) === 0).sort(byAsc);
        const tomorrowArr = eventsFuture.filter((e: any) => dayDiff(e) === 1).sort(byAsc)
        const futureArr = eventsFuture.filter((e: any) => {
          const d = dayDiff(e);
          return d >= 2 && d <= 6;
        }).sort(byAsc);

        const todayLabel = dateFormat(anchor);
        const tomorrowLabel = dateFormat(anchor.clone().add(1, 'day'));

        const sortedData = [
          ...todayArr,
          ...tomorrowArr,
          ...futureArr
        ]

        setUpComing7DaysEvents(sortedData);
        setPast7DaysCompletedEvents(sortByDateDesc(pastCompletedFiltered));
        setPast3MonthsUnCompletedEvents(sortByDateDesc(pastUncompletedFiltered));
        setRowsData([...sortedData, ...pastCompletedFiltered, ...pastUncompletedFiltered]);
        return sortedData;
      });
    })();
  }

  const getEventListParams = () => {
    let queryFilters: any = {
      userFilter: 1,
      orderBy1Desc: false,
      orderBy1Field: 'dateFrom',
      anyTime: 0,
      folderId: '',
      projectId: '',
      activityId: '',
      dateOfInterest: 0,
      standardStatus: '',
      rangeEnd: '',
      rangeStart: '',
      groupUsers: 1,
      userId: store.UserId,
    };

    switch (selectedItem?.type) {
      case FPADataTypes.FOLDER:
        queryFilters.folderId = String(selectedItem?.id);
        break;
      case FPADataTypes.PROJECT:
        queryFilters.folderId = String(selectedItem?.parent_id) || '';
        queryFilters.projectId = String(selectedItem?.id) || '';
        break;
      case FPADataTypes.ACTIVITY:
        queryFilters.folderId = selectedItem?.parent?.parent_id ? String(selectedItem?.parent?.parent_id) : '';
        queryFilters.projectId = String(selectedItem?.parent_id) || '';
        queryFilters.activityId = String(selectedItem?.id) || '';
        break;
    }

    return queryFilters;
  }

  const loadEvents = async (statusArray: any, standardStatus: any, rangeStart: Date, rangeEnd: Date, eventType: string) => {
    try {
      if (eventType === 'future') {
        rangeStart.setHours(0, 0, 0);
        rangeEnd.setHours(23, 59, 59);
      }

      let startDateFormatted = ''
      let endDateFormatted = ''
      startDateFormatted = moment(rangeStart).format('YYYY-MM-DDTHH:mm:ss').toString();
      endDateFormatted = moment(rangeEnd).format('YYYY-MM-DDTHH:mm:ss').toString();
      setShowLoader(true);

      let queryFilters = getEventListParams();

      queryFilters.standardStatus = standardStatus;
      queryFilters.rangeEnd = endDateFormatted;
      queryFilters.rangeStart = startDateFormatted;
      let eventRes: any = await schedulerService.listEvent(
        new ListEventRequest(store.Server, store.SessionId, {
          ...queryFilters,
        })
      );
      if (eventRes?.result == 'OK' && eventRes.ITEMS) {
        let eventData: any = eventRes.ITEMS.ITEM.map((item: any) => ({
          id: item.ident + '_' + Math.floor(Math.random() * 10000),
          ident: item.ident,
          dateTo: moment(item.dateTo).format('MM/DD/YYYY | HH:mm A'),
          title: item.title,
          entryTypeName: item.entryTypeName,
          user: item.USER,
          userList: item.userList,
          solutionStatus: getStatusColor(statusArray, item.solutionStatus),
          icon: getEventIcon(eventType),
          apa: item.apa,
          // statusColor: getStatusBackGroundColor(eventType),
          projectName: item.projectName,
          folderName: item.folderName,
          activityName: item.activityName,
          location: item.location
        }));
        return eventData;
      } else {
        return [];
      }
    } finally {
      setShowLoader(false);
    }
  };

  const getEventIcon = (eventType: string) => {
    if (eventType === 'future') {
      return <EventTaskFutureIcon size={32} />;
    } else if (eventType === 'pastCompleted') {
      return <EventTaskCompletedIcon size={32} />;
    } else if (eventType === 'pastUnCompleted') {
      return <EventTaskUncompletedIcon size={32} />;
    }
  };

  const handleTableRowClick = async (rowId: any) => {
    const item = rowsData.find((x: any) => x.id === rowId);
    setSelectedKeys(new Set([item.ident]));
    const task = await getEvent(item.ident)
    setShowAdd(true);
    setIsEdit(true);
    setShowAddEditRow(false);
    // if (selectedKeys?.has(item.ident) || item === null) {
    //   return;
    // } else {
    //   setSelectedKeys(new Set([item.ident]));
    //   const task = await getEvent(rowId)
    //   setShowAdd(true);
    //   setIsEdit(true);
    //   setShowAddEditRow(false);
    // }
  };

  const handleEditClick = async (item: any) => {
    setSelectedKeys(new Set([item.ident]));
    await getEvent(item.ident);
    setShowAdd(true);
    setIsEdit(true);
    setShowAddEditRow(false);
  }

  const handleAddNew = ()=>{
    setIsEdit(false);
    setShowAdd(true);
    setShowAddEditRow(false);
  }

  const fmtDate = (dt?: string | null) =>
    dt ? moment(dt).format('MM/DD/YYYY') : undefined;
  const fmtTime = (dt?: string | null) =>
    dt ? moment(dt).format('HH:mm') : undefined;

  function normalizeEventTask(payload: any) {
    if (!payload) return null;
    const root = payload.ITEM && typeof payload.ITEM === "object"
      ? payload.ITEM
      : payload;

    if (!root || typeof root !== "object") {
      return null;
    }

    let baseEvent: any = null;
    const users: any[] = [];
    let repeat: any = null;

    const keys = Object.keys(root);
    const numericKeys = keys.filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));

    if (numericKeys.length > 0) {
      numericKeys.forEach((key, index) => {
        const entry = root[key];
        if (!entry || typeof entry !== "object") return;

        if (index === 0) {
          const { USER, REPEAT, ...rest } = entry;
          baseEvent = { ...rest };

          if (USER) users.push(USER);
          if (REPEAT) repeat = REPEAT;
        } else {
          if (entry.USER) users.push(entry.USER);
        }
      });

      if (root.REPEAT) {
        repeat = root.REPEAT;
      }
    } else {
      const { USER, REPEAT, ...rest } = root;
      baseEvent = { ...rest };

      if (USER) users.push(USER);
      if (REPEAT) repeat = REPEAT;
    }

    if (!baseEvent) {
      return null;
    }

    return {
      ...baseEvent,
      users,
      repeat: repeat || null,
      result: payload.result,
      taskIdent: payload.ident,
      taskName: payload.name
    };
  }

  function getClosedStatusIdent(statusList: any[]) {
      const candidates = (statusList || [])
        .filter(s => s?.ident && typeof s.ident === "string")
        .filter(s => s.ident !== "All states")      // ignore fake row
        .filter(s => (s.title || "").trim() !== ""); // ignore blanks
  
      const closed = candidates.find(s => CLOSED_TITLES.has(normalizeText(s.title)));
  
      console.log('close status', closed)
  
      return closed;
    }

  const getEvent = async (selectedId: any, selectedRow?: any) => {
    try {
      // setShowLoaderDetail(true);
      const eventResult = await schedulerService.getEvent(
        new GetEventRequest(
          store.Server,
          store.SessionId,
          selectedId.toString(),
          store.UserId.toString()
        )
      );

      if (eventResult.result !== 'OK') return;
      const items = normalizeEventTask(eventResult.ITEM);
      const single = items ?? null;
      // const statusItem = eventStatusList.find((item: any) => item.title === 'Uzavřen');
      // 

      const statusItem = getClosedStatusIdent(eventStatusList);
      const statusId = statusItem ? statusItem.ident : '';

      const eventDetail = selectedRow ? selectedRow : rowsData.find((x: any) => x.ident === selectedId.toString());
      const result = single && single.ident
        ? {
          id: single.ident ?? single?.id,
          ident: single.ident ?? single?.id + '_' + Math.floor(Math.random() * 10000),
          title: single.title,
          entryTypeName: single.entryTypeName ?? eventDetail?.entryTypeName,
          user: eventDetail?.user,
          userList: eventDetail?.userList,
          solutionStatus: single.solutionStatus,
          status: single.solutionStatus,
          statusName: statusItem ? statusItem.title : '',
          statusId: statusItem ? statusItem.ident : '',
          icon: eventDetail?.icon,
          apa: single.apa ?? eventDetail?.apa,
          statusColor: eventDetail?.statusColor,
          activityId: single.activityId,
          activityName: single.activityName ?? eventDetail?.activityName,
          projectId: single.projectId,
          projectName: single.projectName ?? eventDetail?.projectName,
          folderId: single.folderId,
          folderName: single.folderName ?? eventDetail?.folderName,
          location: single.location,
          description: single.description,
          headId: single.headId,
          repeat: single.repeat,
          dateFrom: fmtDate(single.dateFrom),
          dateTo: fmtDate(single.dateTo),
          timeFrom: fmtTime(single.dateFrom),
          timeTo: fmtTime(single.dateTo),
          isChargeable: single?.chargeable === '1',
          isPrivate: single?.isPrivate === '1',
          isRepeat: single?.repeat ? true : false,
          isResolved: single?.solutionStatus === statusId,
        }
        : null;

      if (!result) return;
      setSelectedDataRow({ ...single, isRepeat: single?.repeat ? true : false, isChargeable: single?.chargeable === '1', isPrivate: single?.isPrivate === '1' });
      setSelectedKeys(new Set([eventDetail?.id ?? single.id]));
      return result;
    } catch (err) {
      console.error('getLayout error:', err);
    } finally {
      // setShowLoaderDetail(false);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View position={'relative'} width={'100%'} minHeight={containerHeight}>
        <Flex direction={'column'} gap={'size-150'} position={'absolute'} top={0} left={0} right={0} width={'100%'} UNSAFE_style={{ zIndex: 1 }}>
          {/*<Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
             <Content position={'relative'} UNSAFE_className={styles.heading_text}>
              Event List
            </Content> 
          </Flex>*/}
          <Flex direction={'column'} width={'100%'} UNSAFE_style={{ maxWidth: '100%' }}>

            {/* <TableView
              UNSAFE_className="tblLayoutComponent"
              aria-label="Event Future List"
              selectionMode="single"
              selectionStyle="highlight"
              width={'100%'}
              height={tableHeight}
              UNSAFE_style={tableStyles}
            >
              <TableHeader columns={isMobile ? columnsDataMobile : columnsData}>
                {column => (
                  <Column showDivider key={column.key} width={column?.width}>
                    {column.name}
                  </Column>
                )}
              </TableHeader>
              <TableBody items={rowsData}>{item => <Row>{columnKey => <Cell>{(item as any)[String(columnKey)]}</Cell>}</Row>}</TableBody>
            </TableView> */}
            <EventListTable
              upComming7DaysEvents={upComing7DaysEvents}
              past7DaysCompleted={past7DaysCompletedEvents}
              past3MonthsUnCompleted={past3MonthsUnCompletedEvents}
              handleTableRowclick={handleTableRowClick}
              handleEditClick={handleEditClick}
              handleAddNew={() => handleAddNew()}
              isShowAddEditButton={true} />
          </Flex>
        </Flex>
        <DialogContainer onDismiss={handleAddNewClose}>
          {showAdd && <AddUpdateEvent
            event={selectedDataRow}
            isEdit={isEdit}
            onClose={handleAddNewClose}
            refreshDetailView={refreshDetailView}
            handleRefreshAddNewEvent={handleRefreshAddNewEvent}
            typeOfWorkList={typeOfWorkList}
            eventStatusList={eventStatusList}
            userList={userList}
            context={getEventListParams()} />
          }
        </DialogContainer>
      </View>
    );
  }
}

export const EventsListComponent = LayoutComponent(EventsList);
